// A withdrawal request holds the patient in two halves: WF67's request stops the workflows, the
// Withings archive stops the charts. This keeps them in step (spec 2026-10-26 §10).
import { useRef } from 'react';
import { useRequestWithdrawal, useCancelWithdrawalRequest } from '@/state/data/carePeriod';
import { useArchivePatient, useUnarchivePatient } from '@/state/data/patient';
import {
  saveRequestThenArchive, unarchiveThenCancel, requestCarePeriodId,
  type SaveRequestOutcome, type CancelRequestOutcome,
} from '@/logic/withdrawalRequest';
import type { CompletionActor } from '@/logic/task';

interface RequestRow {
  task_id?: string | number;
  id_care_period?: unknown;
  care_period_id?: unknown;
}

export default function useWithdrawalHold(withingsUserId: string | number) {
  const requestMut = useRequestWithdrawal();
  const cancelMut = useCancelWithdrawalRequest();
  const archiveMut = useArchivePatient();
  const unarchiveMut = useUnarchivePatient();
  // Which request's charts this window already restarted: a retry after a failed event skips the un-archive.
  const unarchivedFor = useRef<string | null>(null);
  const uid = String(withingsUserId);

  function save(carePeriodId: string, withdrawalReason: string, actor?: CompletionActor): Promise<SaveRequestOutcome> {
    return saveRequestThenArchive({
      request: () => requestMut.mutateAsync({
        withingsUserId: uid, carePeriodId, withdrawalReason, actor,
      }),
      archive: () => archiveMut.mutateAsync({ userid: uid }),
    });
  }

  async function cancel(request: RequestRow, actor?: CompletionActor): Promise<CancelRequestOutcome> {
    const cp = requestCarePeriodId(request);
    if (request.task_id == null || !cp) return 'not_sent';
    const key = String(request.task_id);
    const res = await unarchiveThenCancel({
      unarchive: () => unarchiveMut.mutateAsync({ userid: uid }),
      cancel: () => cancelMut.mutateAsync({
        withingsUserId: uid, carePeriodId: cp, taskId: request.task_id!, actor,
      }),
      unarchived: unarchivedFor.current === key,
    });
    unarchivedFor.current = res.unarchived && res.outcome !== 'cancelled' ? key : null;
    return res.outcome;
  }

  async function setCharts(on: boolean): Promise<boolean> {
    try {
      if (on) {
        await unarchiveMut.mutateAsync({ userid: uid });
      } else {
        await archiveMut.mutateAsync({ userid: uid });
        unarchivedFor.current = null;
      }
      return true;
    } catch {
      return false;
    }
  }

  return {
    save,
    cancel,
    stopCharts: () => setCharts(false),
    resumeCharts: () => setCharts(true),
    busy: requestMut.isPending || cancelMut.isPending || archiveMut.isPending || unarchiveMut.isPending,
  };
}
